import { useContext, useEffect, useRef, useState } from 'react';
import deepEqual from 'fast-deep-equal';
import { AppContext } from "../AppContext";
var EMPTY_PARAMS = {};
var INITIAL_REQUEST_RESULT = {
    data: null,
    meta: {
        done: false,
        loading: false
    }
};
export var useRequest = function (proxy, _a) {
    var _b = _a === void 0 ? {} : _a, autoLoad = _b.autoLoad, wait = _b.wait, mapper = _b.mapper, params = _b.params, filterQuery = _b.filterQuery, onCatchError = _b.onCatchError, onDone = _b.onDone, onError = _b.onError;
    var isActive = !!autoLoad;
    if (autoLoad && wait && wait.length) {
        isActive = wait.every(function (i) { return i.meta.done; });
    }
    var proxyRef = useRef(proxy);
    var unsubscribeRef = useRef(null);
    var paramsRef = useRef(null);
    var filterQueryRef = useRef(null);
    var appContext = useContext(AppContext);
    var _c = useState(false), loading = _c[0], setLoading = _c[1];
    var _d = useState(false), done = _d[0], setDone = _d[1];
    var _e = useState(''), error = _e[0], setError = _e[1];
    var _f = useState(null), totalCount = _f[0], setTotalCount = _f[1];
    if (!proxyRef.current) {
        proxyRef.current = proxy;
    }
    var internalOnCatchError = onCatchError || appContext.getErrorListener().onCatchError;
    if (typeof proxyRef.current.setContext === 'function') {
        proxyRef.current.setContext(appContext.getApiRequest());
    }
    var setParams = function (newParams) {
        if (!deepEqual(paramsRef.current, newParams || EMPTY_PARAMS)) {
            paramsRef.current = newParams || EMPTY_PARAMS;
        }
    };
    setParams(params || EMPTY_PARAMS);
    var setFilterQuery = function (newFilterQuery) {
        if (newFilterQuery === void 0) { newFilterQuery = {}; }
        if (!deepEqual(filterQueryRef.current, newFilterQuery)) {
            filterQueryRef.current = newFilterQuery;
        }
    };
    setFilterQuery(filterQuery || null);
    var requestResultRef = useRef(null);
    var _g = useState(INITIAL_REQUEST_RESULT), result = _g[0], setResult = _g[1];
    var load = function (config) {
        if (config === void 0) { config = {}; }
        setLoading(true);
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
        }
        return new Promise(function (resolve) {
            var subscription = proxyRef.current
                .load(config.payload || null, config.params || paramsRef.current, config.filterQuery || filterQueryRef.current)
                .subscribe(function (value) {
                var newData = value.data;
                var totalCount = value.meta.totalCount;
                var prevResult = result;
                requestResultRef.current = mapper ? mapper(newData) : newData;
                var newResult = {
                    data: requestResultRef.current,
                    meta: {
                        totalCount: totalCount,
                        loading: false,
                        done: true
                    }
                };
                setError('');
                setResult(function (prev) {
                    prevResult = prev;
                    return newResult;
                });
                setTotalCount(totalCount !== null && totalCount !== void 0 ? totalCount : null);
                setLoading(false);
                setDone(true);
                resolve(newResult);
                if (onDone) {
                    onDone(newResult, prevResult);
                }
            }, function (err) {
                var newResult = {
                    data: requestResultRef.current,
                    meta: {
                        loading: false,
                        done: true,
                        error: err.message
                    }
                };
                setError(err.displayMessage || err.message);
                setResult(newResult);
                setLoading(false);
                resolve(newResult);
                internalOnCatchError(err);
                if (onError) {
                    onError(err);
                }
            });
            unsubscribeRef.current = function () {
                subscription.unsubscribe();
            };
        });
    };
    useEffect(function () {
        return function () {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
        };
    }, []);
    useEffect(function () {
        if (isActive) {
            load();
        }
    }, [isActive, paramsRef.current, filterQueryRef.current]);
    var finalResult = {
        data: result.data,
        meta: {
            loading: loading,
            done: done
        }
    };
    if (totalCount) {
        finalResult.meta.totalCount = totalCount;
    }
    if (error) {
        finalResult.meta.error = error;
    }
    var terminate = function () {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
    };
    return [finalResult, { load: load, getResultData: function () { return requestResultRef.current; }, terminate: terminate }];
};
//# sourceMappingURL=useRequest.js.map